<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
// Manual de regras §1.2: a interface DEVE mostrar claramente as duas
// informações — quem é o dono do lead e quem conduz o atendimento agora.
// Antes da separação as duas eram a mesma pessoa na prática, então a tela
// mostrava só "responsável" e ninguém percebia a diferença. Agora que a
// transferência de atendimento não move a carteira, elas divergem de verdade
// e precisam estar visíveis lado a lado.
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import AssignmentTransferRequestsAPI from 'dashboard/api/aceleres/assignmentTransferRequests';

const store = useStore();
const currentChat = computed(() => store.getters.getSelectedChat);

const portfolioOwner = ref(null);
const currentAssignee = ref(null);
const pendingAttendanceRequest = ref(null);

const load = async () => {
  const conversationId = currentChat.value?.id;
  if (!conversationId) {
    portfolioOwner.value = null;
    currentAssignee.value = null;
    pendingAttendanceRequest.value = null;
    return;
  }

  // allSettled: cada lado é opcional. Uma falha na carteira não pode esconder
  // o atendente, e vice-versa.
  const [ownership, attendance] = await Promise.allSettled([
    ConversationTransferRequestsAPI.status(conversationId),
    AssignmentTransferRequestsAPI.status(conversationId),
  ]);

  if (Number(currentChat.value?.id) !== Number(conversationId)) return;

  portfolioOwner.value =
    ownership.status === 'fulfilled'
      ? ownership.value?.data?.payload?.current_owner || null
      : null;
  const attendancePayload =
    attendance.status === 'fulfilled' ? attendance.value?.data?.payload : null;
  currentAssignee.value = attendancePayload?.current_assignee || null;
  pendingAttendanceRequest.value = attendancePayload?.pending_request || null;
};

const isDivergent = computed(
  () =>
    portfolioOwner.value?.id &&
    currentAssignee.value?.id &&
    Number(portfolioOwner.value.id) !== Number(currentAssignee.value.id)
);

const hasAnything = computed(
  () => portfolioOwner.value || currentAssignee.value
);

watch(() => currentChat.value?.id, load, { immediate: true });
// A aprovação de uma transferência troca o assignee sem recarregar a tela.
watch(() => currentChat.value?.meta?.assignee?.id, load);
</script>

<template>
  <!-- Aceleres: as duas informacoes ficam empilhadas, cada uma com o rotulo
       em cima e o nome em destaque embaixo, separadas por um filete. A caixa
       com borda e fundo que existia aqui competia com os cartoes do painel
       (dados do contato, midia) e ficava pesada na coluna estreita. -->
  <section
    v-show="hasAnything"
    data-testid="aceleres-ownership-summary"
    class="mx-4 mb-3"
  >
    <div class="py-2">
      <p class="text-xs leading-tight text-n-slate-10">Dono do lead</p>
      <p class="mt-0.5 text-[0.9375rem] font-semibold text-n-slate-12">
        {{ portfolioOwner?.name || 'Sem dono definido' }}
      </p>
    </div>
    <div class="border-t border-n-weak py-2">
      <p class="text-xs leading-tight text-n-slate-10">Atendendo</p>
      <p class="mt-0.5 text-[0.9375rem] font-semibold text-n-slate-12">
        {{ currentAssignee?.name || 'Sem responsável' }}
      </p>
    </div>
    <p v-if="isDivergent" class="text-xs leading-snug text-n-slate-10">
      Atendimento emprestado: a carteira segue com
      {{ portfolioOwner?.name }}.
    </p>
    <p
      v-if="pendingAttendanceRequest"
      class="mt-1 text-xs leading-snug text-n-amber-11"
    >
      Transferência para {{ pendingAttendanceRequest.target_assignee?.name }}
      aguardando o gestor.
    </p>
  </section>
</template>
